/**
 * Created by Ning Jiang on 10/19/2018.
 */

import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {IMultiSpritesConfig, MultiSprites} from "@nolimitcity/slot-game/bin/core/displayobject/MultiSprites";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {IBetWayWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {ParsedGameData} from "@nolimitcity/slot-game/bin/core/server/data/ParsedGameData";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {SpinEvent} from "@nolimitcity/slot-game/bin/core/spin/event/SpinEvent";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {TextStyleOptions} from "@nolimitcity/slot-game/bin/core/text/TextStyleOptions";
import {TimelineLite, TweenLite} from "gsap";
import {TombstoneGameConfig} from "../../TombstoneGameConfig";

export class WinWaysIndicator extends BaseView {

    public static readonly SHOW_WIN_WAYS_WON:string = "winWaysIndicatorEvent_showWinWaysWon";

    private readonly _waysWonNumberStype:TextStyleOptions = {
        textBaseline : "middle",
        fontFamily : "Durango Western",
        fontSize : 58,
        fill : "#0a1030",
        lineHeight : 116
    };
    private readonly _wayWonTexts:string[] = ["wayWon", "waysWon"];
    private readonly _winWaysPlateFadeInDuration:number = 0.5;
    private readonly _winWaysNumberFadeInDelay:number = 0.3;
    private readonly _winWaysNumberFadeInDuration:number = 0.5;
    private readonly _hideDelay:number = 0.8;
    private readonly _hideDuration:number = 0.2;

    private _layer:ScalableLayer;
    private _staticWinWays:PIXI.Sprite;
    private _waysWonContainer:PIXI.Container;
    private _waysWonNumber:PIXI.Text;
    private _waysWonText:MultiSprites;
    private _showTimeline:TimelineLite;

    private _winWaysTotal:number;

    constructor() {
        super();
        this._layer = StageManager.getLayer(TombstoneGameConfig.instance.LAYERS.WIN_WAYS.name);
    }

    protected addEventListeners():void {
        EventHandler.addEventListener(this, ServerEvent.GAME_DATA_PARSED, (event:GameEvent) => this.onGameDataParsed(event.params[0]));
        EventHandler.addEventListener(this, WinWaysIndicator.SHOW_WIN_WAYS_WON, (event:GameEvent) => this.onShowWinWaysWon(event.params[0]));
        EventHandler.addEventListener(this, SpinEvent.BEFORE_START, (event:GameEvent) => this.onHideWinWaysWon());
    }

    protected onGameDataParsed(data:ParsedGameData):void {
        this._winWaysTotal = 0;
        for(let i:number = 0; i < data.betWins.length; i++) {
            const betWayWin:IBetWayWinData = <IBetWayWinData>data.betWins[i];
            this._winWaysTotal += betWayWin.numberOfCombinations;
        }
    }

    protected initAnimations():void {
        this._staticWinWays = new PIXI.Sprite(GameResources.getTextures("108winWays")[0]);
        this._staticWinWays.position.set(146, 242);
        this._layer.addChild(this._staticWinWays);

        this._waysWonContainer = new PIXI.Container();
        this._waysWonContainer.position.set(1052, 247);
        this._layer.addChild(this._waysWonContainer);

        const waysWonPlate = new PIXI.Sprite(GameResources.getTextures("waysWonPlate")[0]);
        this._waysWonContainer.addChild(waysWonPlate);

        const waysWonConfig:IMultiSpritesConfig = {};
        this._wayWonTexts.forEach((text:string) => {
            waysWonConfig[text] = {
                textures : GameResources.getTextures(`${text}Text`),
                anchor : [0.5, 0.5]
            }
        });
        this._waysWonText = new MultiSprites(waysWonConfig);
        this._waysWonText.position.set(42, 150);
        this._waysWonText.visible = false;
        this._waysWonContainer.addChild(this._waysWonText);

        this._waysWonNumber = new PIXI.Text("", this._waysWonNumberStype);
        this._waysWonNumber.anchor.set(0.5, 0.5);
        this._waysWonNumber.alpha = 0;
        this._waysWonNumber.position.set(42, 68);
        this._waysWonContainer.addChild(this._waysWonNumber);

        // test:
        // this.onShowWinWaysWon(108, false);
    }

    private onShowWinWaysWon(winWaysWon?:number, animate?:boolean):void {
        winWaysWon = winWaysWon != undefined ? winWaysWon : this._winWaysTotal;
        animate = animate != undefined ? animate : true;
        const wayWonText:string = winWaysWon === 1 ? this._wayWonTexts[0] : this._wayWonTexts[1];

        if(this._waysWonNumber.alpha > 0 && this._waysWonNumber.text === `${winWaysWon}`) {
            return;
        }

        if(this._showTimeline && this._showTimeline.isActive()) {
            this._showTimeline.progress(1);
        }

        const timeline:TimelineLite = new TimelineLite({paused : true});

        let numberDelay:number = 0;
        if(!this._waysWonText.visible) {
            // Show plate
            timeline.add([
                () => {this._waysWonText.visible = true;},
                this._waysWonText.playSprite(wayWonText,
                    {
                        duration : animate ? this._winWaysPlateFadeInDuration : 0,
                        vars : [{fromVars : {alpha : 0}, toVars : {alpha : 1}}]
                    })
            ], 0);
            numberDelay = animate ? this._winWaysNumberFadeInDelay : 0;
        } else {
            // Update plate
            timeline.add(this._waysWonText.playSprite(wayWonText, {duration : 0, vars : [{toVars : {alpha : 1}}]}), 0);
        }

        timeline.add([
            TweenLite.fromTo(this._waysWonNumber, animate ? this._winWaysNumberFadeInDuration : 0, {alpha : 0}, {alpha : 1}),
            () => {
                this._waysWonNumber.text = "" + winWaysWon;
            }
        ], numberDelay);

        this._showTimeline = timeline;
        this._showTimeline.play();
    }

    private onHideWinWaysWon():void {
        if(this._winWaysTotal > 0) {
            if(this._showTimeline && this._showTimeline.isActive()) {
                this._showTimeline.progress(1);
            }
            this.onShowWinWaysWon(this._winWaysTotal, false);

            const stopTimeline:TimelineLite = new TimelineLite();
            stopTimeline.add([
                TweenLite.to(this._waysWonNumber, 0.2, {alpha : 0}),
                this._waysWonText.playSprite(
                    this._winWaysTotal === 1 ? this._wayWonTexts[0] : this._wayWonTexts[1],
                    {
                        duration : this._hideDuration,
                        vars : [{toVars : {alpha : 0}}]
                    })
            ], this._hideDelay);
            stopTimeline.add(() => {
                this._waysWonText.visible = false;
            });
        }
    }
}